import { useParams } from 'react-router-dom'
import { ExternalLink, MapPin } from 'lucide-react'
import { Alerta, Boton, Cargando, Panel } from '../../components/kit'
import { BadgesBien } from '../../components/inventario/BadgesBien'
import { rangoCarpeta } from '../../components/inventario/GaleriaFotos'
import { useFichaPublica } from '../../queries/useInventario'
import { urlFotoFicha, urlMiniaturaFicha } from '../../services/fotoService'
import { mensajeDeError } from '../../utils/errores'
import { formatoFecha } from '../../utils/fecha'
import { ESTADO_FISICO, TIPO, ubicacion } from '../../utils/inventario'

/**
 * La ficha que abre el QR de la etiqueta (`/b/<clave>`): datos, oficina y fotos, sin el
 * resto de la app. Pide sesión pero no un rol. Quien ve el inventario tiene además el
 * enlace a la ficha interna (historial, traslados, edición).
 */
export default function FichaBien() {
  const clave = useParams().clave ?? ''
  const ficha = useFichaPublica(clave)

  if (ficha.isLoading) return <Cargando />
  if (ficha.isError || !ficha.data) return <Alerta tipo="error">{mensajeDeError(ficha.error, 'No se encontró el bien.')}</Alerta>
  const b = ficha.data
  const fotos = b.carpetas.reduce((n, c) => n + c.fotos.length, 0)

  return (
    <div className="pila pila--amplia ficha-qr">
      <header className="pagina__cabecera pagina__cabecera--con-accion">
        <div>
          <h1 className="dcc-t-heading-m">{b.numero || 'Sin N°'} · {b.descripcion}</h1>
          <BadgesBien bien={b} />
        </div>
        {b.id != null && (
          <div className="acciones-pagina">
            <Boton variante="outlined" icono={ExternalLink} to={`/app/bienes/${b.id}`}>Abrir en el inventario</Boton>
          </div>
        )}
      </header>

      {b.estado === 'NO_UBICADO' && (
        <Alerta tipo="error" titulo="No ubicado">No estaba en {b.recinto?.nombre ?? 'su oficina'} cuando se revisó. Si lo encontraste, avisa a quien administra el inventario.</Alerta>
      )}
      {b.estado === 'BAJA' && <Alerta tipo="info" titulo="Para dar de baja">Este bien está marcado para darse de baja.</Alerta>}

      <div className="grilla">
        <Panel titulo="Ubicación">
          {b.recinto ? (
            <p className="ficha-qr__lugar">
              <MapPin className="dcc-i" size={20} aria-hidden />
              <span>
                <strong>{b.recinto.nombre}</strong>
                <span className="tabla__sub">{b.recinto.codigo} · {ubicacion(b.recinto)}</span>
              </span>
            </p>
          ) : (
            <p className="texto-secundario">Sin ubicación registrada.</p>
          )}
        </Panel>
        <Panel titulo="Datos">
          <dl className="datos">
            <dt>N° de bien</dt>
            <dd>{b.numero || '—'}</dd>
            <dt>Tipo</dt>
            <dd>{TIPO[b.tipo].texto}</dd>
            <dt>Descripción</dt>
            <dd>{b.descripcion}</dd>
            <dt>Marca</dt>
            <dd>{b.marca || '—'}</dd>
            <dt>Modelo</dt>
            <dd>{b.modelo || '—'}</dd>
            <dt>N° de serie</dt>
            <dd>{b.serie || '—'}</dd>
            <dt>Estado físico</dt>
            <dd>{ESTADO_FISICO[b.estado_fisico].texto}</dd>
            <dt>Observación</dt>
            <dd className="texto-preformateado">{b.observacion || '—'}</dd>
            <dt>Último cambio</dt>
            <dd>{formatoFecha(b.fecha_modificacion)}</dd>
          </dl>
        </Panel>
      </div>

      <Panel titulo={fotos ? `Fotos (${fotos})` : 'Fotos'}>
        {!fotos && <p className="texto-secundario">Todavía no hay fotos de este bien.</p>}
        <div className="pila">
          {b.carpetas.map((c) => (
            <section key={c.inicio} className="pila">
              <h3 className="dcc-t-body-m ficha-qr__carpeta">{rangoCarpeta(c)}</h3>
              <ul className="galeria__grilla">
                {c.fotos.map((f) => (
                  // La foto entera en otra pestaña: el celular la amplía mejor que un visor.
                  <li key={f.id}>
                    <a className="galeria__foto" href={urlFotoFicha(b.clave, f.id)} target="_blank" rel="noopener" aria-label={`Ver ${f.nombre}`}>
                      <img src={urlMiniaturaFicha(b.clave, f.id)} alt={f.observacion || f.nombre} loading="lazy" />
                      {f.observacion && <span className="galeria__obs">{f.observacion}</span>}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Panel>
    </div>
  )
}
